import type { Match } from "../MatchCards/types";
import { hasValidOdds } from "../../Trader/oddsUtils";
import {
  CRICKET_TEST_FORMAT_CODE,
  findCricketThreeWayMatchWinnerMarket as findCricketThreeWayMatchWinnerMarketUtil,
  findCricketTieSelection as findCricketTieSelectionUtil,
  findCricketMatchWinnerThirdSelection as findCricketMatchWinnerThirdSelectionUtil,
  cricketMatchWinnerThirdRowLabel as cricketMatchWinnerThirdRowLabelUtil,
  isCricketDrawSelectionName as isCricketDrawSelectionNameUtil,
  isCricketTieOrDrawSelectionName as isCricketTieOrDrawSelectionNameUtil,
  hasCricketThreeWayMatchWinnerMarket as hasCricketThreeWayMatchWinnerMarketUtil,
  isCricketTestFormatRaw,
  isCricketTestMatch as isCricketTestMatchCore,
  isCricketTestMatchFromText,
  normalizeCricketFormatCode,
} from "../../../utils/cricketTestMatch";

/**
 * Find market by sport type
 * Used for trade slip handlers to find the appropriate market (Match Odds, Moneyline, WIN, etc.)
 */
export const findMarketBySport = (match: Match, sport: string): any => {
    if (!match.markets || match.markets.length === 0) {
        return null;
    }

    const sportLower = sport.toLowerCase();
    const markets = match.markets;

    if (sportLower === "tennis") {
        return markets.find((market: any) => {
            const marketName = (market.name || "").toLowerCase();
            const marketType = (market.market_type || "").toLowerCase();
            return marketName === "moneyline" || marketType === "money_line" || marketType === "moneyline";
        });
    } else if (sportLower === "football") {
        return markets.find((market: any) => {
            const marketName = (market.name || "").toLowerCase();
            const marketType = (market.market_type || "").toLowerCase();
            return marketName === "match odds" || marketType === "one_x_two";
        });
    } else if (sportLower === "horses") {
        return markets.find((market: any) => {
            const marketName = (market.name || "").toLowerCase();
            const marketType = (market.market_type || "").toLowerCase();
            return marketName === "win" || marketType === "outright_ded_fact";
        });
    } else if (sportLower === "cricket") {
        return markets.find((market: any) => {
            const id = String(market.market_type_id ?? "");
            const marketType = (market.market_type || "").toLowerCase();
            return id === "10" || id === "1010" || marketType.includes("match winner");
        });
    }

    return null;
};

/**
 * Parse a value to a number for odds (handles API returning string odds).
 */
const parseOddsNumber = (value: unknown): number | null => {
    if (value == null || value === "") return null;
    const n = typeof value === "number" ? value : parseFloat(String(value));
    return hasValidOdds(n) ? n : null;
};

/**
 * Get odds from runner for display (Football, Tennis, Horses).
 * Prefers runner.odds (backend sends ladder-rounded value) then runner.price;
 * only falls back to max back from prices when neither is set.
 * Ensures match cards, market displays and trade slips all show the same ladder values as backend.
 */
export const getOddsFromRunner = (runner: any): number | null => {
    if (runner == null) return null;
    const fromOdds = parseOddsNumber(runner?.odds ?? runner?.price);
    if (fromOdds != null) return fromOdds;
    const prices = runner.prices || [];
    const backPrices = prices.filter((price: any) => price.side === "back");
    if (backPrices.length > 0) {
        const parsed = backPrices.map((p: any) => parseOddsNumber(p.odds)).filter((n): n is number => n != null);
        if (parsed.length > 0) return Math.max(...parsed);
    }
    return null;
};

/**
 * Find matching runner for a participant
 * Matches a participant to a runner in a market by various criteria
 */
export const findMatchingRunner = (
    runners: any[],
    participant: any
): any => {
    if (!runners || runners.length === 0 || !participant) {
        return null;
    }

    return runners.find(
        (runner: any) =>
            runner["event-participant-id"] === participant.id ||
            runner.name === participant.name ||
            runner.name === participant["participant-name"] ||
            (participant["participant-name"] && runner.name?.includes(participant["participant-name"])) ||
            (participant.name && runner.name?.includes(participant.name))
    );
};

/** Draw runner in football Match Odds (1X2). */
export const findDrawRunner = (runners: any[] | undefined | null): any => {
    if (!runners?.length) return null;
    return (
        runners.find((runner) => {
            const name = (runner.name || runner["participant-name"] || "").trim().toLowerCase();
            return name === "draw" || name === "the draw" || name === "x";
        }) ?? null
    );
};

/** When only one Match Odds runner is offerable, UI shows SUS on all three. */
export const shouldFootballMatchOddsSuspendThird = (
    runners: any[] | undefined | null,
    isMarketSuspended: boolean,
): boolean => {
    if (isMarketSuspended || !runners?.length) return false;
    const validCount = runners.filter((runner) => hasValidOdds(getOddsFromRunner(runner))).length;
    return validCount === 1;
};

export const getCricketMatchFormat = (match: any, streamData?: any): number | null => {
    return normalizeCricketFormatCode(
        match?.format ??
            match?.Format ??
            streamData?.match_update?.format ??
            streamData?.event?.format,
    );
};

export { CRICKET_TEST_FORMAT_CODE, isCricketTestFormatRaw, isCricketTestMatchFromText };

export const findCricketThreeWayMatchWinnerMarket = findCricketThreeWayMatchWinnerMarketUtil;
export const findCricketTieSelection = findCricketTieSelectionUtil;
export const findCricketMatchWinnerThirdSelection = findCricketMatchWinnerThirdSelectionUtil;
export const cricketMatchWinnerThirdRowLabel = cricketMatchWinnerThirdRowLabelUtil;
export const isCricketTieOrDrawSelectionName = isCricketTieOrDrawSelectionNameUtil;
export const hasCricketThreeWayMatchWinnerMarket = hasCricketThreeWayMatchWinnerMarketUtil;

export const findCricketTwoWayMatchWinnerMarket = (markets: any[] | undefined | null): any => {
    const list = markets || [];
    const preferred = list.find((market) => String(market.market_type_id) === "1010");
    if (preferred) return preferred;
    return list.find((market) => String(market.market_type_id) === "10") ?? null;
};

export const isCricketTestMatch = (match: any, streamData?: any): boolean =>
    isCricketTestMatchCore({
        match,
        streamData,
        competition: match?.competition ?? streamData?.event?.competition,
        matchName: match?.name,
    });

/** Decimal `active` / `display` must be explicitly 1 (missing counts as off). */
const isDecimalActiveOne = (value: unknown): boolean => value === 1 || value === "1";

/**
 * True when Decimal has actually offered Match Winner 3-way (type 15) for lobby display:
 * market active, Draw runner, two team runners, and at least one active team with a usable price.
 *
 * Unlimited-overs / first-class feeds often keep a suspended type-15 row (trader margin still
 * fills recommended_price from probability) while only Draw No Trade (10/1010) is tradable —
 * those must not count as "received" or the lobby shows SUS instead of DNB prices.
 */
export const isCricketThreeWayMatchWinnerReceived = (market: any): boolean => {
    if (!market || String(market.market_type_id) !== "15") return false;
    if (!isDecimalActiveOne(market.active)) return false;
    const selections = market.selectionslist ?? market.selections;
    if (!Array.isArray(selections) || selections.length < 3) return false;
    const drawSelection = selections.find((selection) =>
        isCricketDrawSelectionNameUtil(selection?.name),
    );
    if (!drawSelection || !isDecimalActiveOne(drawSelection.active)) return false;
    const teamSelections = selections.filter(
        (selection) => !isCricketTieOrDrawSelectionNameUtil(selection?.name),
    );
    if (teamSelections.length < 2) return false;
    return teamSelections.some((selection) => {
        if (!isDecimalActiveOne(selection?.active)) return false;
        return parseOddsNumber(selection?.recommended_price) != null;
    });
};

/**
 * Lobby Match Winner:
 * - Test / unlimited overs: prefer priced 3-way (incl. Draw); if 3-way was never
 *   received, fall back to Draw No Trade (2-way 10/1010) so main list shows DNB prices.
 * - Limited overs: 2-way only.
 */
export const findCricketMatchWinnerMarket = (
    markets: any[] | undefined | null,
    isTest: boolean,
): any => {
    const twoWay = findCricketTwoWayMatchWinnerMarket(markets);
    const threeWay = findCricketThreeWayMatchWinnerMarket(markets);
    if (isTest) {
        if (isCricketThreeWayMatchWinnerReceived(threeWay)) return threeWay;
        return twoWay ?? threeWay ?? null;
    }
    return twoWay ?? null;
};

/** 3-way Match Winner used for the Tie row (market_type_id 15). */
export const findCricketMatchWinnerMarketForTie = (markets: any[] | undefined | null): any =>
    findCricketThreeWayMatchWinnerMarket(markets);
